import PropTypes from 'prop-types';
import React, { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';
import { useFeatures } from 'utils/util/features';
import UnifiedSummaryGenerationCard from 'views/Engagement/UnifiedSummaryGenerationCard';
import EngagementSurveySummaryCard from 'views/EngagementSurvey/EngagementSurveySummaryCard';
import EngagementSurveySummaryTaskCard from 'views/EngagementSurvey/EngagementSurveySummaryTaskCard';
import BreakdownChartCard from '../../Cards/BreakdownChartCard';
import CandlestickChartCard from '../../Cards/CandlestickChartCard';
import Container from '../../Cards/Container';
import DistributionInfoCard from '../../Cards/DistributionInfoCard';
import EmployeeNPSResultsCard from '../../Cards/EmployeeNPSResultsCard';
import GenericDoughnutCard from '../../Cards/GenericDoughnutCard';
import GenericTableCard from '../../Cards/GenericTableCard';
import LeaderboardCard from '../../Cards/LeaderboardCard';
import MultichartCard from '../../Cards/MultichartCard';
import PowerLawCard from '../../Cards/PowerLawCard';
import ProgressChartCard from '../../Cards/ProgressChartCard';
import ProgressTableCard from '../../Cards/ProgressTableCard';
import ScatterPlotCard from '../../Cards/ScatterPlotCard';
import SimpleCard from '../../Cards/SimpleCard';
import SkillCloudsCard from '../../Cards/SkillCloudsCard';
import TripleLeaderboardCard from '../../Cards/TripleLeaderboardCard';
import EmptyState from '../../EmptyState';

export const CARD_TYPES = {
  PROGRESS_CHART: 0,
  PROGRESS_TABLE: 1,
  DISTRIBUTION_INFO: 2,
  SIMPLE_CARD: 3,
  BREAKDOWN_CHART: 4,
  LEADERBOARD_CARD: 5,
  SKILL_CLOUDS_CARD: 6,
  SCATTER_PLOT: 7,
  HEADER: 8,
  POWER_LAW_CARD: 9,
  CANDLESTICK_CHART: 10,
  TRIPLE_LEADERBOARD_CARD: 11,
  GENERIC_TABLE: 12,
  GENERIC_DOUGHNUT: 13,
  CHORD: 14,
  SANKEY: 15,
  NETWORK: 16,
  HORIZONTAL_BAR: 17,
  MULTICHART: 18,
  EMPLOYEE_NPS: 19,
  ENGAGEMENT_SURVEY_SUMMARY: 20,
  CONTAINER: 21,
  UNIFIED_SUMMARY_GENERATION: 22,
};

const getCounters = (data, key) => {
  if (!data || !key) {
    return undefined;
  }
  key.split('.').forEach((k) => {
    if (k in data) {
      data = data[k];
    } else {
      return undefined;
    }
  });
  return data;
};

const CardRowDashboard: FC<Props> = (props) => {
  const { formatMessage } = useIntl();
  const { isEnabled } = useFeatures();
  const outputToCard = useCallback(
    (output) =>
      output.type === CARD_TYPES.PROGRESS_TABLE ? (
        // @ts-expect-error
        <ProgressTableCard {...output} data={props.data[output.dataKey]} />
      ) : output.type === CARD_TYPES.PROGRESS_CHART ? (
        // @ts-expect-error
        <ProgressChartCard {...output} data={props.data[output.dataKey]} />
      ) : output.type === CARD_TYPES.DISTRIBUTION_INFO ? (
        <DistributionInfoCard
          {...output}
          counters={getCounters(props.data, output.dataKey)}
          baseline={getCounters(props.data, output.baselineKey)}
        />
      ) : output.type === CARD_TYPES.SIMPLE_CARD ? (
        // @ts-expect-error
        <SimpleCard {...output}>{props.data[output.datakey]}</SimpleCard>
      ) : output.type === CARD_TYPES.BREAKDOWN_CHART ? (
        // @ts-expect-error
        <BreakdownChartCard {...output} data={props.data[output.datakey]} />
      ) : output.type === CARD_TYPES.LEADERBOARD_CARD ? (
        // @ts-expect-error
        <LeaderboardCard {...output} data={props.data[output.datakey]} />
      ) : output.type === CARD_TYPES.SKILL_CLOUDS_CARD ? (
        // @ts-expect-error
        <SkillCloudsCard {...output} data={props.data[output.datakey]} />
      ) : output.type === CARD_TYPES.SCATTER_PLOT ? (
        <ScatterPlotCard
          {...output}
          // @ts-expect-error
          data={props.data[output.datakey]}
          showMetric={false}
          showField={false}
        />
      ) : output.type === CARD_TYPES.HEADER ? (
        // @ts-expect-error
        <div {...output}>{props.data[output.datakey]}</div>
      ) : output.type === CARD_TYPES.POWER_LAW_CARD ? (
        // @ts-expect-error
        <PowerLawCard {...output} data={props.data[output.datakey]} />
      ) : output.type === CARD_TYPES.CANDLESTICK_CHART ? (
        <CandlestickChartCard
          {...output}
          // @ts-expect-error
          data={props.data[output.datakey]}
          showMetric={false}
        />
      ) : output.type === CARD_TYPES.TRIPLE_LEADERBOARD_CARD ? (
        // @ts-expect-error
        <TripleLeaderboardCard {...output} data={props.data[output.datakey]} />
      ) : output.type === CARD_TYPES.GENERIC_TABLE ? (
        // @ts-expect-error
        <GenericTableCard {...output} rows={props.data[output.dataKey]} />
      ) : output.type === CARD_TYPES.GENERIC_DOUGHNUT ? (
        // @ts-expect-error
        <GenericDoughnutCard {...output} data={props.data[output.dataKey]} />
      ) : output.type === CARD_TYPES.MULTICHART ? (
        <MultichartCard {...output} data={output?.content} />
      ) : output.type === CARD_TYPES.EMPLOYEE_NPS ? (
        // @ts-expect-error
        <EmployeeNPSResultsCard {...output} data={props.data[output.datakey]} />
      ) : output.type === CARD_TYPES.ENGAGEMENT_SURVEY_SUMMARY ? (
        isEnabled('engagement_survey_summaries') ? (
          <EngagementSurveySummaryTaskCard {...output} />
        ) : (
          <EngagementSurveySummaryCard {...output} summary={output?.summary} />
        )
      ) : output.type === CARD_TYPES.CONTAINER ? (
        // @ts-expect-error
        <Container>{props.data[output.datakey]}</Container>
      ) : output.type === CARD_TYPES.UNIFIED_SUMMARY_GENERATION ? (
        <UnifiedSummaryGenerationCard {...output} />
      ) : (
        <EmptyState
          title={formatMessage({
            id: 'app.views.widgets.dashboards.card_row_dashboard.title.unsupported_card_type',
            defaultMessage: 'Unsupported card type.',
          })}
        />
      ),
    [props.data, formatMessage, isEnabled]
  );

  return props.data !== undefined ? (
    <>
      {props.beforeContent}
      {props.rows.map((row, index) => (
        <Row xs="1" md="12" key={index}>
          {/* @ts-expect-error */}
          {row.map(
            (output, output_index) =>
              // @ts-expect-error
              output?.skip !== 'true' && (
                <Col
                  // @ts-expect-error
                  className={`col-12 col-md-${12 / row.length}`}
                  key={output_index}
                >
                  {outputToCard(output)}
                </Col>
              )
          )}
        </Row>
      ))}
    </>
  ) : (
    // @ts-expect-error
    <EmptyState title={props.emptyStateText} />
  );
};

const CardRowDashboard_propTypes = {
  rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.object)).isRequired,
  data: PropTypes.object,
  pretitle: PropTypes.string,
  underTitle: PropTypes.object,
  mainActionElement: PropTypes.node,
  beforeContent: PropTypes.node,
  emptyStateText: PropTypes.string,
};

type Props = PropTypes.InferProps<typeof CardRowDashboard_propTypes>;

export default React.memo(CardRowDashboard);
